import { useState } from 'react';
import { format } from 'date-fns';
import { useTaskCustomFields, normalizeFieldValue } from '@/hooks/useTaskCustomFields';
import type { FieldType, CustomFieldEntry } from '@/hooks/useTaskCustomFields';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Plus, X, Pencil, Check, Settings2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TaskCustomFieldsSectionProps {
  taskId: string;
  readOnly?: boolean;
}

export const TaskCustomFieldsSection = ({ taskId, readOnly = false }: TaskCustomFieldsSectionProps) => {
  const { customFields, setField, removeField, isUpdating } = useTaskCustomFields(taskId);
  const [isAdding, setIsAdding] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [newType, setNewType] = useState<FieldType>('text');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editType, setEditType] = useState<FieldType>('text');

  const INTERNAL_FIELDS = ['auto_type', 'linked_sale_id', 'linked_loan_id'];
  const entries = Object.entries(customFields).filter(([key]) => !INTERNAL_FIELDS.includes(key));

  const renderFieldValue = (entry: CustomFieldEntry) => {
    switch (entry.type) {
      case 'number':
        return (
          <span className="text-sm">
            {entry.value != null ? Number(entry.value).toLocaleString('pt-BR') : '—'}
          </span>
        );
      case 'date':
        return (
          <span className="text-sm">
            {entry.value
              ? format(new Date(String(entry.value) + 'T12:00:00'), 'dd/MM/yyyy')
              : '—'}
          </span>
        );
      case 'checkbox':
        return (
          <Badge variant={entry.value ? 'default' : 'outline'} className="text-xs h-5">
            {entry.value ? 'Sim' : 'Não'}
          </Badge>
        );
      default:
        return (
          <span className="text-sm truncate max-w-[160px]">
            {String(entry.value ?? '')}
          </span>
        );
    }
  };

  const renderValueInput = (
    type: FieldType,
    value: string,
    onChange: (v: string) => void,
    autoFocus = false
  ) => {
    if (type === 'checkbox') {
      const checked = value === 'true';
      return (
        <div className="flex items-center gap-2 h-8">
          <Switch
            checked={checked}
            onCheckedChange={(c) => onChange(String(c))}
          />
          <span className="text-xs text-muted-foreground">{checked ? 'Sim' : 'Não'}</span>
        </div>
      );
    }
    return (
      <Input
        type={type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'}
        placeholder={type === 'number' ? '0' : type === 'date' ? '' : 'Valor'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoFocus={autoFocus}
        className="h-8 text-sm"
      />
    );
  };

  const handleAdd = () => {
    const trimmedKey = newKey.trim();
    if (!trimmedKey || isUpdating) return;
    const typedValue =
      newType === 'number'   ? (newValue !== '' ? Number(newValue) : null) :
      newType === 'checkbox' ? (newValue === 'true') :
      newValue;
    setField(trimmedKey, { type: newType, value: typedValue });
    setNewKey('');
    setNewValue('');
    setNewType('text');
    setIsAdding(false);
  };

  const handleStartEdit = (key: string, rawValue: any) => {
    const normalized = normalizeFieldValue(rawValue);
    setEditingKey(key);
    setEditValue(String(normalized.value ?? ''));
    setEditType(normalized.type);
  };

  const handleSaveEdit = () => {
    if (!editingKey) return;
    const typedValue =
      editType === 'number'   ? (editValue !== '' ? Number(editValue) : null) :
      editType === 'checkbox' ? (editValue === 'true') :
      editValue;
    setField(editingKey, { type: editType, value: typedValue });
    setEditingKey(null);
  };

  const handleCancelEdit = () => {
    setEditingKey(null);
    setEditValue('');
    setEditType('text');
  };

  return (
    <div className="space-y-3">
      <label className="text-sm font-medium flex items-center gap-2">
        <Settings2 className="h-4 w-4" />
        Campos Personalizados
      </label>

      {entries.length > 0 && (
        <div className="space-y-2">
          {entries.map(([key, rawValue]) => {
            const entry = normalizeFieldValue(rawValue);
            return (
              <div key={key} className="flex items-center gap-2 group">
                <span className="text-sm font-medium text-muted-foreground min-w-[100px] truncate">
                  {key}
                </span>
                {editingKey === key ? (
                  <>
                    {renderValueInput(editType, editValue, setEditValue, true)}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 flex-shrink-0"
                      onClick={handleSaveEdit}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveEdit();
                        if (e.key === 'Escape') handleCancelEdit();
                      }}
                    >
                      <Check className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 flex-shrink-0"
                      onClick={handleCancelEdit}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </>
                ) : (
                  <>
                    <span
                      className={cn(
                        "flex-1 min-w-0",
                        !readOnly && "cursor-pointer hover:text-primary"
                      )}
                      onClick={() => !readOnly && handleStartEdit(key, rawValue)}
                    >
                      {renderFieldValue(entry)}
                    </span>
                    {!readOnly && (
                      <>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                          onClick={() => handleStartEdit(key, rawValue)}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-destructive flex-shrink-0"
                          onClick={() => removeField(key)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!readOnly && (
        isAdding ? (
          <div className="space-y-2">
            {/* Linha 1: nome do campo */}
            <Input
              placeholder="Nome do campo"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              className="h-8 text-sm"
              autoFocus
            />
            {/* Linha 2: tipo + valor */}
            <div className="flex items-center gap-2">
              <Select
                value={newType}
                onValueChange={(v) => { setNewType(v as FieldType); setNewValue(''); }}
              >
                <SelectTrigger className="w-28 h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Texto</SelectItem>
                  <SelectItem value="number">Número</SelectItem>
                  <SelectItem value="date">Data</SelectItem>
                  <SelectItem value="checkbox">Checkbox</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex-1">
                {renderValueInput(newType, newValue, setNewValue)}
              </div>
            </div>
            {/* Linha 3: ações */}
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-8"
                onClick={() => { setIsAdding(false); setNewKey(''); setNewValue(''); setNewType('text'); }}
              >
                Cancelar
              </Button>
              <Button
                size="sm"
                className="h-8"
                onClick={handleAdd}
                disabled={!newKey.trim() || isUpdating}
              >
                Salvar
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => setIsAdding(true)}
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Adicionar Campo
          </Button>
        )
      )}
    </div>
  );
};
